import api from '@pray/shared/utils/api';

const ADMIN_URL = '/web/studio/pray-radio/sunday-services';
const artistUrl = (artistId: string) => `/web/studio/artists/${artistId}/pray-radio/sunday-services`;
const serviceDateQuery = (serviceDate: string) => new URLSearchParams({ service_date: serviceDate }).toString();

type SundayServiceRange = {
  startDate: string;
  endDate: string;
};

// Admins see every leader's Sunday through the admin URL; the per-leader URL is the one the edits go through.
export async function getPrayRadioSundayServices({
  artistId,
  startDate,
  endDate,
  all = false,
}: SundayServiceRange & { artistId?: string; all?: boolean }) {
  if (!startDate) throw Error('Start Date is required');
  if (!endDate) throw Error('End Date is required');
  if (!all && !artistId) throw Error('Artist ID is required');

  const base = all ? ADMIN_URL : artistUrl(artistId as string);
  const params = new URLSearchParams({ start_date: startDate, end_date: endDate });
  const response = await api.executeGetRequest(`${base}?${params.toString()}`);

  return response.data;
}

export async function assignPrayRadioSundayService({
  serviceDate,
  artistId,
}: {
  serviceDate: string;
  artistId: string;
}) {
  if (!serviceDate) throw Error('Service Date is required');
  if (!artistId) throw Error('Artist ID is required');

  const response = await api.executePutRequest(ADMIN_URL, { service_date: serviceDate, artist_id: artistId });

  return response.data;
}

export async function unassignPrayRadioSundayService({ serviceDate }: { serviceDate: string }) {
  if (!serviceDate) throw Error('Service Date is required');

  const response = await api.executeDeleteRequest(`${ADMIN_URL}?${serviceDateQuery(serviceDate)}`);

  return response.data;
}

type SundayServiceContentSource = { sourceContentId?: string; sourceDailyItemId?: string };

export async function setPrayRadioSundayServiceContent({
  artistId,
  serviceDate,
  sourceContentId,
  sourceDailyItemId,
}: { artistId: string; serviceDate: string } & SundayServiceContentSource) {
  if (!artistId) throw Error('Artist ID is required');
  if (!serviceDate) throw Error('Service Date is required');
  if (!sourceContentId && !sourceDailyItemId) throw Error('A content or daily item is required');

  const response = await api.executePutRequest(`${artistUrl(artistId)}/content`, {
    service_date: serviceDate,
    ...(sourceContentId ? { source_content_id: sourceContentId } : {}),
    ...(sourceDailyItemId ? { source_daily_item_id: sourceDailyItemId } : {}),
  });

  return response.data;
}

export async function clearPrayRadioSundayServiceContent({
  artistId,
  serviceDate,
}: {
  artistId: string;
  serviceDate: string;
}) {
  if (!artistId) throw Error('Artist ID is required');
  if (!serviceDate) throw Error('Service Date is required');

  const response = await api.executeDeleteRequest(`${artistUrl(artistId)}/content?${serviceDateQuery(serviceDate)}`);

  return response.data;
}

// Mirrors uploadPrayTvContent with audio fields: the file is already in storage, the server creates the
// Sunday Service content from it, titled "<leader> on Sunday Service".
export async function uploadPrayRadioSundayServiceContent({
  artistId,
  serviceDate,
  primaryAudioUrl,
  primaryAudioFileDuration,
  primaryAudioFileSize,
  audioFileName,
}: {
  artistId: string;
  serviceDate: string;
  primaryAudioUrl: string;
  primaryAudioFileDuration: number;
  primaryAudioFileSize: number;
  audioFileName?: string;
}) {
  if (!artistId) throw Error('Artist ID is required');
  if (!serviceDate) throw Error('Service Date is required');

  const response = await api.executePostRequest(`${artistUrl(artistId)}/upload`, {
    service_date: serviceDate,
    primary_audio_url: primaryAudioUrl,
    primary_audio_file_duration: primaryAudioFileDuration,
    primary_audio_file_size: primaryAudioFileSize,
    ...(audioFileName && { audio_file_name: audioFileName }),
  });

  return response.data;
}

// Admin only: points the content airing for the Sunday at another audio file (the finished Omny episode). The
// content keeps its id; only the file the apps play changes.
export async function replacePrayRadioSundayServiceAudio({
  artistId,
  serviceDate,
  audioUrl,
  duration,
}: {
  artistId: string;
  serviceDate: string;
  audioUrl: string;
  duration?: number | null;
}) {
  if (!artistId) throw Error('Artist ID is required');
  if (!serviceDate) throw Error('Service Date is required');
  if (!audioUrl) throw Error('Audio URL is required');

  const response = await api.executePutRequest(`${artistUrl(artistId)}/audio`, {
    service_date: serviceDate,
    audio_url: audioUrl,
    ...(duration ? { duration } : {}),
  });

  return response.data;
}

export type SundayServiceImageUrls = {
  squareImageUrl?: string;
  landscapeImageUrl?: string;
  portraitImageUrl?: string;
};

// Admin only: puts the designed cover art on the content airing for the Sunday. Each variant given replaces
// the one of that label on the server; the others stay.
export async function setPrayRadioSundayServiceImages({
  artistId,
  serviceDate,
  squareImageUrl,
  landscapeImageUrl,
  portraitImageUrl,
}: { artistId: string; serviceDate: string } & SundayServiceImageUrls) {
  if (!artistId) throw Error('Artist ID is required');
  if (!serviceDate) throw Error('Service Date is required');
  if (!squareImageUrl && !landscapeImageUrl && !portraitImageUrl) throw Error('An image is required');

  const response = await api.executePutRequest(`${artistUrl(artistId)}/images`, {
    service_date: serviceDate,
    ...(squareImageUrl ? { square_image_url: squareImageUrl } : {}),
    ...(landscapeImageUrl ? { landscape_image_url: landscapeImageUrl } : {}),
    ...(portraitImageUrl ? { portrait_image_url: portraitImageUrl } : {}),
  });

  return response.data;
}
